// Engineer PWA "My expenses" screen (Session ATE, engineer expenses and mileage). Submit a mileage,
// materials or other claim -- general, or for a specific job when reached from that job's "Claim an
// expense for this job" link (?jobId=) -- and see the status of the engineer's own claims.
//
// LIVE-ONLY, DELIBERATELY NOT QUEUED THROUGH THE OFFLINE OUTBOX (stated here, per the working
// discipline, not left implicit): a job sheet must survive being captured underground and submitted
// hours later, which is why that outbox exists. An expense claim is filed with signal, at the end of a
// visit or the end of the day, and a second offline queue for it would be exactly the over-engineering
// the house rules warn against for a one-engineer, one-admin business. Submit is gated on being online,
// the same shape MyTimesheet's own Submit uses for the same reason.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { prepareImageForUpload } from "../../lib/imageUpload";
import { ApiError } from "../../lib/api";
import { expensesApi, EXPENSE_TYPES, EXPENSE_TYPE_LABEL, estimatedMileageAmountPence, MILEAGE_RATE_PENCE_PER_MILE, type Expense, type ExpenseType } from "../../lib/expenses";
import { useAuth } from "../../lib/auth";
import { hasOwnEngineerRecords, isOversightAccount } from "../../lib/pwaIdentity";
import { presentableServerMessage } from "../../lib/serverMessage";
import { OversightNotice } from "./OversightNotice";
import { useEngineerData } from "./EngineerDataContext";

const inputCls = "w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-ele-strong outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]";
const labelCls = "mb-1 block font-menu text-xs tracking-wide text-ele-body";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const STATUS_LABEL: Record<Expense["status"], string> = { submitted: "Submitted", approved: "Approved", rejected: "Rejected" };
const STATUS_TONE: Record<Expense["status"], string> = {
  submitted: "border-ele-card-border text-ele-body",
  approved: "border-[#63C41F] text-[var(--ele-green-ink)]",
  rejected: "border-ele-danger text-ele-danger",
};

function gbpPence(p: number): string {
  return `£${(p / 100).toFixed(2)}`;
}

export function MyExpenses() {
  const { online } = useEngineerData();
  const { user } = useAuth();
  const ownRecords = hasOwnEngineerRecords(user);
  const [params] = useSearchParams();
  const jobId = params.get("jobId") || undefined;

  const [type, setType] = useState<ExpenseType>("mileage");
  const [date, setDate] = useState(todayIso());
  const [description, setDescription] = useState("");
  const [miles, setMiles] = useState("");
  const [amount, setAmount] = useState(""); // pounds, as typed
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [justSubmitted, setJustSubmitted] = useState(false);

  const [mine, setMine] = useState<Expense[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const loadMine = useCallback(() => {
    if (!ownRecords) return;
    expensesApi
      .list()
      .then((res) => setMine(res.expenses))
      .catch((err: unknown) => setListError(err instanceof ApiError ? presentableServerMessage(err.message) : "Could not load your expenses."));
  }, [ownRecords]);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  if (!ownRecords) {
    return (
      <OversightNotice
        title="My expenses"
        explanation={
          isOversightAccount(user)
            ? "You are signed in with an office account, which has no expenses of its own. Review every engineer's claims on the office Expenses page."
            : "This account has no engineer profile, so it has no expenses of its own."
        }
        officeHref={isOversightAccount(user) ? "/office/expenses" : undefined}
        officeLabel={isOversightAccount(user) ? "Open office expenses" : undefined}
      />
    );
  }

  const estimatedMileageAmount = type === "mileage" && miles ? estimatedMileageAmountPence(Number(miles)) : null;

  async function submit() {
    setFormError(null);
    if (!description.trim()) {
      setFormError("Say what this expense was for.");
      return;
    }
    if (type === "mileage") {
      const m = Number(miles);
      if (!miles || !Number.isFinite(m) || m <= 0) {
        setFormError("Enter the distance claimed, in miles.");
        return;
      }
    } else {
      const a = Number(amount);
      if (!amount || !Number.isFinite(a) || a <= 0) {
        setFormError("Enter the amount claimed.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const created = await expensesApi.create(
        type === "mileage"
          ? { type: "mileage", jobId, date, description: description.trim(), milesClaimed: Number(miles) }
          : { type, jobId, date, description: description.trim(), amountPence: Math.round(Number(amount) * 100) },
      );
      if (receiptFile) {
        try {
          // A library photo can be HEIC: made a JPEG here, at the moment of sending, so it is never
          // stored in a format a report cannot show.
          const toSend = await prepareImageForUpload(receiptFile);
          await expensesApi.uploadReceipt(created.id, toSend, toSend.name || "receipt.jpg");
        } catch {
          // The claim itself is already submitted; a failed receipt upload is surfaced but never
          // rolled back the create -- the office can still see and decide the claim without it.
          setFormError("Your expense was submitted, but the receipt photo could not be uploaded. You can try again from the office if needed.");
        }
      }
      setDescription("");
      setMiles("");
      setAmount("");
      setReceiptFile(null);
      setJustSubmitted(true);
      loadMine();
    } catch (err) {
      setFormError(err instanceof ApiError ? presentableServerMessage(err.message) : "Could not submit this expense. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-md p-4 sm:p-6">
      <h1 className="mb-1 font-heading text-xl text-ele-blue">My expenses</h1>
      {jobId && <p className="mb-3 text-xs text-ele-body">Claiming against this job. Leave the app's back button to claim a general expense instead.</p>}

      <section className="rounded-ele border border-ele-card-border bg-ele-card p-4">
        <div className="space-y-3">
          <div>
            <label className={labelCls} htmlFor="expense-type">
              Type
            </label>
            <select id="expense-type" value={type} onChange={(e) => setType(e.target.value as ExpenseType)} className={inputCls}>
              {EXPENSE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {EXPENSE_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="expense-date">
              Date
            </label>
            <input id="expense-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
          </div>
          {type === "mileage" ? (
            <div>
              <label className={labelCls} htmlFor="expense-miles">
                Miles claimed
              </label>
              <input id="expense-miles" type="number" min="0" step="0.1" inputMode="decimal" value={miles} onChange={(e) => setMiles(e.target.value)} className={inputCls} />
              {estimatedMileageAmount != null && (
                <p className="mt-1 text-xs text-ele-body">
                  Estimated: {gbpPence(estimatedMileageAmount)} at {MILEAGE_RATE_PENCE_PER_MILE}p/mile
                </p>
              )}
            </div>
          ) : (
            <div>
              <label className={labelCls} htmlFor="expense-amount">
                Amount (£)
              </label>
              <input id="expense-amount" type="number" min="0" step="0.01" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} />
            </div>
          )}
          <div>
            <label className={labelCls} htmlFor="expense-description">
              What was this for?
            </label>
            <textarea id="expense-description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="expense-receipt">
              Receipt photo (optional)
            </label>
            <input
              id="expense-receipt"
              type="file"
              accept="image/*"
              onChange={(e) => setReceiptFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm text-ele-body"
            />
          </div>
        </div>

        {formError && (
          <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">
            {formError}
          </p>
        )}
        {justSubmitted && !formError && (
          <p role="status" className="mt-3 rounded-ele border border-[#63C41F] px-3 py-2 text-sm text-[var(--ele-green-ink)]">
            Expense submitted.
          </p>
        )}
        {!online && <p className="mt-3 text-xs text-ele-body">You are offline. Submitting needs a connection.</p>}

        <button
          type="button"
          onClick={() => void submit()}
          disabled={submitting || !online}
          className="mt-4 min-h-11 w-full rounded-ele bg-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-bg disabled:opacity-50"
        >
          {submitting ? "Submitting..." : "Submit expense"}
        </button>
      </section>

      <section className="mt-6">
        <h2 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">My claims</h2>
        {listError && (
          <p role="alert" className="text-sm text-ele-danger">
            {listError}
          </p>
        )}
        {mine === null && !listError && <p className="text-sm text-ele-body">Loading...</p>}
        {mine !== null && mine.length === 0 && <p className="text-sm text-ele-body">No expenses claimed yet.</p>}
        {mine && mine.length > 0 && (
          <ul className="space-y-2">
            {mine.map((e) => (
              <li key={e.id} className="rounded-ele border border-ele-card-border bg-ele-card p-3 text-sm" data-testid="my-expense-row">
                <p className="flex items-center justify-between">
                  <span className="text-ele-strong">
                    {EXPENSE_TYPE_LABEL[e.type]} · {gbpPence(e.amountPence)}
                  </span>
                  <span className={`rounded-ele border px-2 py-0.5 font-menu text-xs tracking-wide ${STATUS_TONE[e.status]}`}>{STATUS_LABEL[e.status]}</span>
                </p>
                <p className="mt-1 text-xs text-ele-body">
                  {e.date} · {e.jobLabel ?? "General"}
                </p>
                <p className="mt-1 text-xs text-ele-body">{e.description}</p>
                {e.status === "rejected" && e.rejectionReason && <p className="mt-1 text-xs text-ele-danger">Rejected: {e.rejectionReason}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
